import React,{ useState, useEffect, useMemo, useRef } from "react";
import { useLocation, NavLink } from "react-router-dom";
import {
  ExpandLess,
  ExpandMore,
  // CheckCircle as SubscribedIcon,
  // Cancel as NotSubscribedIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Button,
  Tooltip,
  Popover,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Logo from "../assets/logo.png";
import IconLogo from "../assets/smalllogo.svg";
import ServicesService from "../services/ServicesService";
import { navSections } from "../config/navSections";
import SidebarCollapseToggle from "./SidebarCollapseToggle";

const SIDEBAR_WIDTH = 235;
const COLLAPSED_WIDTH = 76;

const navItemSx = (collapsed, isSubItem) => ({
  mx: collapsed ? 0.75 : 1.25,
  my: 0.35,
  px: collapsed ? 1.25 : undefined,
  pl: collapsed ? undefined : isSubItem ? 5.5 : 2,
  py: 1,
  borderRadius: 2.5,
  justifyContent: collapsed ? "center" : "flex-start",
  minHeight: 42,
  color: "var(--theme-sidebar-text-muted)",
  transition: "background-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease",
  ...(isSubItem && !collapsed
    ? {
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 28,
          top: "50%",
          width: 10,
          borderTop: "1.5px solid var(--theme-sidebar-border)",
          transform: "translateY(-50%)",
        },
      }
    : {}),
  "&.Mui-selected": {
    backgroundColor: "#13306ea8 !important" ,//active bg color
    border:"1px solid #1F456E",
    color: "#308aeb !important",
    fontWeight: 600,
    boxShadow: "none",
    "&:hover": { backgroundColor:  "#13306e !important", },//active hover color
  },
  
  "&:hover": {
     backgroundColor: " #13306ea8 !important",//moving hover color
     color: " var(--theme-sidebar-text-muted)", //moving hover text color
  },
});

function filterSectionsByBusinessSetup(sections, businessSetup) {
  const goals = Array.isArray(businessSetup?.goals) ? businessSetup.goals : [];
  const shouldFilterOperations = businessSetup?.completed && goals.length > 0;
  if (!shouldFilterOperations) return sections;

  return sections
    .map((section) => {
      if (section.label !== "Operations") return section;
      const items = section.items.filter((item) => {
        if (item.id === "appointments") return goals.includes("appointments");
        if (item.id === "tickets") return goals.includes("tickets");
        return true;
      });
      return { ...section, items };
    })
    .filter((section) => section.items.length > 0);
}

function isPathActive(pathname, item) {
  if (item.path) {
    return pathname === item.path || pathname.startsWith(item.path + "/");
  }
  if (item.children) {
    return item.children.some((child) => pathname === child.path || pathname.startsWith(child.path + "/"));
  }
  return false;
}

function NavItem({ item, pathname, isSubItem, collapsed, onNavigate }) {
  const isActive = isPathActive(pathname, item);
  const iconSrc = item.activeIcon && item.inactiveIcon ? (isActive ? item.activeIcon : item.inactiveIcon) : null;
  const Icon = item.Icon;

  const button = (
    <ListItemButton
      component={NavLink}
      to={item.path}
      selected={isActive}
      onClick={onNavigate}
      sx={navItemSx(collapsed, isSubItem)}
    >
      {iconSrc ? (
        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, justifyContent: "center" }}>
          <img src={iconSrc} alt="" style={{ width: 20, height: 20 }} />
        </ListItemIcon>
      ) : Icon ? (
        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: "inherit", justifyContent: "center" }}>
          <Icon fontSize="small" />
        </ListItemIcon>
      ) : null}
      {!collapsed && (
        <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: isActive ? 600 : 500, noWrap: true }} />
      )}
    </ListItemButton>
  );

  if (collapsed) {
    return (
      <Tooltip title={item.label} placement="right" arrow>
        <Box component="span" sx={{ display: "flex", width: "100%" }}>
          {button}
        </Box>
      </Tooltip>
    );
  }
  return button;
}

function NavItemWithSubmenu({ item, pathname, open, onToggle, collapsed, onNavigate }) {
  const isParentActive = isPathActive(pathname, item);
  const iconSrc = item.activeIcon && item.inactiveIcon ? (isParentActive ? item.activeIcon : item.inactiveIcon) : null;
  const [anchorEl, setAnchorEl] = useState(null);

  if (collapsed) {
    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    return (
      <>
        <Tooltip title={item.label} placement="right" arrow>
          <Box component="span" sx={{ display: "flex", width: "100%" }}>
            <ListItemButton
              onClick={handleOpen}
              selected={isParentActive}
              sx={navItemSx(true, false)}
            >
            {iconSrc && (
              <ListItemIcon sx={{ minWidth: 0, justifyContent: "center" }}>
                <img src={iconSrc} alt="" style={{ width: 20, height: 20 }} /> 
              </ListItemIcon>
            )}
          </ListItemButton>
          </Box>
        </Tooltip>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: { minWidth: 200, borderRadius: 2, boxShadow: "0 8px 24px rgba(0,0,0,0.35)", border: "1px solid var(--theme-sidebar-border)", bgcolor: "#051d37", color: "var(--theme-sidebar-text)" } } }}
        >
          <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: 12, fontWeight: 700, color: "var(--theme-sidebar-text-muted)", textTransform: "uppercase" }}>
            {item.label}
          </Typography>
          <List disablePadding>
            {item.children.map((child) => (
              <NavItem
                key={child.id}
                item={child}
                pathname={pathname}
                collapsed={false}
                onNavigate={() => {
                  handleClose();
                  onNavigate?.();
                }}
              />
            ))}
          </List>
        </Popover>
      </>
    );
  }

  return (
    <Box>
      <ListItemButton
        onClick={onToggle}
        selected={isParentActive}
        sx={navItemSx(false, false)}
      >
        {iconSrc ? (
          <ListItemIcon sx={{ minWidth: 36 }}>
            <img src={iconSrc} alt="" style={{ width: 20, height: 20,  }} />
          </ListItemIcon>
        ) : null}
        <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: isParentActive ? 600 : 500 }} />
        {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List
          disablePadding
          sx={{
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              left: 28,
              top: 2,
              bottom: 2,
              borderLeft: "1.5px solid var(--theme-sidebar-border)",
            },
          }}
        >
          {item.children.map((child) => (
            <NavItem key={child.id} item={child} pathname={pathname} isSubItem onNavigate={onNavigate} />
          ))}
        </List>
      </Collapse>
    </Box>
  );
}

export default function AppSidebar({
  mobileOpen,
  onClose,
  desktopCollapsed = false,
  onToggleCollapse,
  account,
  onLogout,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const pathname = location.pathname;
  // const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const [businessSetup, setBusinessSetup] = useState(null);
  // const hoverTimerRef = useRef(null);
  const visibleNavSections = useMemo(
    () => filterSectionsByBusinessSetup(navSections, businessSetup),
    [businessSetup]
  );

  const [openSections, setOpenSections] = useState(
    navSections.reduce((acc, s) => ({ ...acc, [s.label]: s.defaultOpen }), {})
  );

  const [openNavItems, setOpenNavItems] = useState(() => {
    const initial = {};
    navSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.children) {
          initial[item.id] = item.children.some(
            (c) => pathname === c.path || pathname.startsWith(c.path + "/")
          );
        }
      });
    });
    return initial;
  });

  useEffect(() => {
    let mounted = true;
    const loadBusinessSetup = () => {
      ServicesService.getServiceSettings()
        .then((settings) => {
          if (!mounted) return;
          setBusinessSetup(settings?.allSettings?.business_setup || null);
        })
        .catch(() => {
          if (mounted) setBusinessSetup(null);
        });
    };
    loadBusinessSetup();
    window.addEventListener("business-setup-updated", loadBusinessSetup);
    return () => {
      mounted = false;
      window.removeEventListener("business-setup-updated", loadBusinessSetup);
    };
  }, []);

  useEffect(() => {
    setOpenNavItems((prev) => {
      let next = { ...prev };
      let changed = false;
      visibleNavSections.forEach((section) => {
        section.items.forEach((item) => {
          if (item.children) {
            const shouldBeOpen = item.children.some(
              (c) => pathname === c.path || pathname.startsWith(c.path + "/")
            );
            if (shouldBeOpen && !prev[item.id]) {
              next[item.id] = true;
              changed = true;
            }
          }
        });
      });
      return changed ? next : prev;
    });
  }, [pathname, visibleNavSections]);

  const toggleSection = (label) => {
    setOpenSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const toggleNavItem = (id) => {
    setOpenNavItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const subscription = account?.getSubscription?.();
  const isSubscribed = subscription && subscription.getStatus() === "active";


  const isSidebarCollapsed = !isMobile && desktopCollapsed;
  const drawerWidth = isSidebarCollapsed ? COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const handleNavClick = () => {
    if (isMobile) onClose?.();
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "var(--theme-sidebar-bg)",
        color: "var(--theme-sidebar-text)",
        position: "relative",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          px: isSidebarCollapsed ? 1.5 : 2,
          py: 2,
          borderBottom: "1px solid var(--theme-sidebar-border)",
          display: "flex",
          alignItems: "center",
          justifyContent: isSidebarCollapsed ? "center" : "flex-start",
          gap: 1.25,
          minHeight: 72,
        }}
      >
        {isSidebarCollapsed ? (
          <Box component="img" src={IconLogo} alt="WappGrow" sx={{ width: 36, height: 36 }} />
        ) : (
          <Box sx={{ minWidth: 0, width: "100%" }}>
            <Box component="img" src={Logo} alt="WappGrow" sx={{ height: 38, width: "auto", maxWidth: "100%", objectFit: "contain", display: "block" }} />
            {account?.getSubscription?.()?.getPlanName?.() && (
              <Typography sx={{ mt: 0.75, fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "#60a5fa" }}>
                {account.getSubscription().getPlanName().toUpperCase()} PLAN
              </Typography>
            )}
            {/* <Typography sx={{ mt: 1.25, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--theme-sidebar-text-muted)" }}>
              Workspace
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, color: "var(--theme-sidebar-text)" }} noWrap>
              {account?.getFirstName?.() ? `${account.getFirstName()}'s team` : "Your workspace"}
            </Typography> */}
          </Box>
        )}
      </Box>

      <List component="nav" sx={{ flex: 1, overflow: "auto", py: 1.5, px: 0 }}>
        {visibleNavSections.map((section, sectionIndex) => (
          <Box key={section.label}>
            {sectionIndex > 0 && isSidebarCollapsed && (
              <Divider sx={{ my: 1, mx: 1.5, borderColor: "var(--theme-sidebar-border)" }} />
            )}
            {!isSidebarCollapsed && (
              <ListItemButton
                onClick={() => toggleSection(section.label)}
                sx={{
                  mx: 1.25,
                  my: 0.75,
                  py: 0.5,
                  minHeight: 32,
                  borderRadius: 1.5,
                  color: "var(--theme-sidebar-text-muted)",
                  "&:hover": { backgroundColor: "#13306ea8 !important", color: "var(--theme-sidebar-text-muted)" },
                }}
              >
                <ListItemText
                  primary={section.label}
                  primaryTypographyProps={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: "inherit",
                  }}
                />
                {openSections[section.label] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </ListItemButton>
            )}
            <Collapse in={isSidebarCollapsed || openSections[section.label]} timeout="auto" unmountOnExit={!isSidebarCollapsed}>
              <List disablePadding>
                {section.items.map((item) =>
                  item.children ? (
                    <NavItemWithSubmenu
                      key={item.id}
                      item={item}
                      pathname={pathname}
                      open={!!openNavItems[item.id]}
                      onToggle={() => toggleNavItem(item.id)}
                      collapsed={isSidebarCollapsed}
                      onNavigate={handleNavClick}
                    />
                  ) : (
                    <NavItem
                      key={item.id}
                      item={item}
                      pathname={pathname}
                      collapsed={isSidebarCollapsed}
                      onNavigate={handleNavClick}
                    />
                  )
                )}
              </List>
            </Collapse>
          </Box>
        ))}
      </List>

      <Divider sx={{ borderColor: "var(--theme-sidebar-border)" }} />

      <Box
        sx={{
          px: isSidebarCollapsed ? 1 : 1.5,
          py: 1.5,
          borderTop: "1px solid var(--theme-sidebar-border)",
        }}
      >
        {/* <Box
          sx={{
            p: 1.25,
            borderRadius: 1.8,
            border: "1px solid var(--theme-sidebar-border, rgba(255,255,255,0.1))",
            backgroundColor: "var(--theme-sidebar-surface, rgba(255,255,255,0.04))",
            mb: 1,
          }}
        > */}
          {/* <Typography
            variant="caption"
            sx={{
              color: "var(--theme-sidebar-text-muted)",
              display: "block",
              mb: 0.8,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Subscription
          </Typography> */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isSubscribed ? (
              <>
                {/* <SubscribedIcon sx={{ fontSize: 18, color: "var(--theme-success, #66bb6a)" }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Subscribed
                </Typography> */}
                {/* {subscription.getPlanName() && (
                  <Typography variant="caption" sx={{ color: "var(--theme-sidebar-text-muted)" }}>
                    ({subscription.getPlanName()})
                  </Typography>
                )} */}
              </>
            ) : (
              <>
                {/* <NotSubscribedIcon sx={{ fontSize: 18, color: "var(--theme-sidebar-text-muted)" }} />
                <Typography variant="body2" sx={{ 
                  fontWeight: 500 ,
                  whiteSpace: "nowrap", 
                  overflow: "hidden",
                  maxWidth: "100%",}}
                >
                Not subscribed
                </Typography> */}
   
              </>
            )}
          </Box>
        {/* </Box> */}
   
        
        {isSidebarCollapsed ? (
          <Tooltip title="Logout" placement="right" arrow>
            <Button
              fullWidth
              variant="text"
              onClick={onLogout}
              aria-label="Logout"
              sx={{
                minWidth: 0,
                justifyContent: "center",
                textTransform: "none",
                borderRadius: 2,
                py: 1,
                color: "var(--theme-sidebar-text-muted)",
                "&:hover": {
                  color: "#f87171",
                  backgroundColor: "transparent",
                },
              }}
            >
              <LogoutIcon fontSize="small" />
            </Button>
          </Tooltip>
        ) : (
          
          
          <Button
            fullWidth
            variant="text"
            onClick={onLogout}
            sx={{
              justifyContent: "center",
              textTransform: "none",
              borderRadius: 2,
              py: 1.1,
              color: "var(--theme-sidebar-text-muted)",
               border: "1px solid var(--theme-sidebar-border, rgba(168, 166, 166, 0.1))",
              borderColor: "#1F456E)",
              fontWeight: 500,
              fontSize: 14,
              "&:hover": {
                color: "#308aeb  ",
                backgroundColor: "#13306ea8",
              },
            }}
          >
            SignOut
          </Button>
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
            top: 0,
            left: 0,
            zIndex: theme.zIndex.drawer + 2,
            borderRight: "1px solid var(--theme-sidebar-border)",
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: drawerWidth,
        flexShrink: 0,
        transition: theme.transitions.create("width", {
          easing: theme.transitions.easing.easeInOut,
          duration: 260,
        }),
      }}
    >
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.easeInOut,
            duration: 260,
          }),
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            top: 0,
            left: 0,
            zIndex: theme.zIndex.drawer + 6,
            overflowX: "hidden",
            borderRight: "1px solid var(--theme-sidebar-border)",
            mt: 0,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.easeInOut,
              duration: 260,
            }),
          },
        }}
      >
        {drawerContent}
      </Drawer>
      {onToggleCollapse && (
        <SidebarCollapseToggle
          collapsed={isSidebarCollapsed}
          onClick={onToggleCollapse}
        />
      )}
    </Box>
  );
}